import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactComponent as ChanakyaArrow } from '../../../public/static/ai/chanakya_logo.svg';
import LiquidEther from '../../components/chanakya/three/LiquidEther';
import GradientText from '../../components/chanakya/three/GradientText';
import ChanakyaScreen from '../../components/chanakya/ChanakyaScreen';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { APP_CONSTANTS } from '../../URLConstants';

// ----------------------------------------------------------------------
export const NOTIFICATION_WIDTH_XL = 345; // 👈 increase here
export const NOTIFICATION_WIDTH_XLM = 400; // 👈 increase here

const APP_BAR_MOBILE = 70;
const APP_BAR_DESKTOP = 82;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
  // background: 'linear-gradient(120deg, #FAFBFF 0%, #C5DEFF 86%)'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP,
    paddingLeft: theme.spacing(9),
    marginRight: theme.spacing(-1)
  }
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { userLoginOrg } = useAuth();
  const [background, setBackground] = useState(
    'linear-gradient(180deg, rgba(119, 200, 255, 0) 0%, rgba(126, 181, 252, 0.63) 25%, rgba(0, 66, 233, 0.48) 86%)'
  );
  const [mode, setMode] = useState('prediction');
  const location = useLocation();
  const isPrediction = mode === 'prediction';
  const [sliding, setSliding] = useState(false);

  const NotificationContainer = styled('div')(({ theme }) => ({
    height: '100vh',
    position: 'fixed',
    right: 0,
    top: 0,
    overflow: 'hidden',
    background: 'transparent',

    /* Default: hidden */
    display: 'none',

    /* Show from lg (1200px) */
    [theme.breakpoints.up('lg')]: {
      display: 'block',
      width: NOTIFICATION_WIDTH_XL,
      minWidth: NOTIFICATION_WIDTH_XL,
      maxWidth: NOTIFICATION_WIDTH_XL
    },

    /* Ultra-wide screens (1680px+) */
    '@media (min-width:1680px)': {
      width: NOTIFICATION_WIDTH_XLM,
      minWidth: NOTIFICATION_WIDTH_XLM,
      maxWidth: NOTIFICATION_WIDTH_XLM
    }
  }));

  return (
    <RootStyle>
      <MainStyle>
        <Outlet />
      </MainStyle>
      {userLoginOrg === APP_CONSTANTS?.SBI_CLIENT_IDENTIFIER && (
        <NotificationContainer>
          <LiquidEther
            colors={['#4AA4E5AA', '#488ee2', '#003CC0DD']}
            mouseForce={15}
            cursorSize={50}
            isViscous={false}
            viscous={30}
            iterationsViscous={32}
            iterationsPoisson={32}
            resolution={0.5}
            isBounce={false}
            autoDemo
            autoSpeed={0.5}
            autoIntensity={1.5}
            takeoverDuration={0.25}
            autoResumeDelay={3000}
            autoRampDuration={0.6}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 0 // background
            }}
          />
          <Container
            sx={{
              position: 'relative',
              height: '100%',
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                pt: 2.2,
                pb: 1
              }}
            >
              {/* Top Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 20
                }}
              >
                <ChanakyaArrow />

                <Box sx={{ pt: 0.5 }}>
                  <GradientText
                    colors={['#4087FF', '#9642EE', '#DA0CE1', '#4087FF', '#9642EE']}
                    animationSpeed={3}
                    showBorder={false}
                    style={{ fontSize: '20px', fontWeight: 700 }}
                  >
                    Chanakya AI
                  </GradientText>
                </Box>
              </Box>

              {/* <Box
                onClick={() => {
                  setSliding(true); 
                  setTimeout(() => {
                    setMode(isPrediction ? 'insight' : 'prediction');
                    setBackground(
                      !isPrediction
                        ? 'linear-gradient(180deg, rgba(119, 200, 255, 0) 0%, rgba(126, 181, 252, 0.63) 25%, rgba(0, 66, 233, 0.48) 86%)'
                        : 'linear-gradient(180deg, rgba(168, 178, 241, 0) 0%, rgba(157, 168, 235, 0.63) 40%, rgba(72, 55, 195, 0.48) 100%)'
                    );

                    setSliding(false); 
                  }, 900);
                }}
                sx={{
                  width: 120,
                  height: 40,
                  borderRadius: '40px',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isPrediction ? 'flex-start' : 'flex-end',
                  px: 1.2,
                  transition: 'all 0.3s ease',
                  background: isPrediction
                    ? 'linear-gradient(90deg, #76A8F8, #0D6CF2)'
                    : 'linear-gradient(90deg, #A595F5C2, #985DF6AD)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'white',
                    position: 'absolute',
                    left: 8,
                    animation: sliding
                      ? `${
                          isPrediction ? 'glideRight' : 'glideLeft'
                        } 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`
                      : 'none',
                    transform: isPrediction ? 'translateX(0)' : 'translateX(76px)',

                    boxShadow: '0px 3px 10px rgba(0,0,0,0.2)'
                  }}
                />

                <Typography
                  sx={{
                    width: '100%',
                    textAlign: isPrediction ? 'right' : 'left',
                    pr: isPrediction ? '6px' : 0,
                    pl: isPrediction ? 0 : '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'white',
                    zIndex: 2,
                    userSelect: 'none'
                  }}
                >
                  {isPrediction ? 'Prediction' : 'Insight'}
                </Typography>
              </Box> */}
            </Box>
            <ChanakyaScreen setBackground={setBackground} isPrediction={isPrediction} />
            {/* <ChanakyaScreen
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10, // foreground
                pointerEvents: 'auto'
              }}
              mode={mode}
            /> */}
          </Container>
        </NotificationContainer>
      )}
    </RootStyle>
  );
}
